import { PaymentMethodEnum } from "../models/types.mjs";
import { SubscriptionStatus } from "../models/subscription.mjs";
import { extractPaymentMethodInputSnapshot } from "./snapshot.mjs";
import { getSubscriptionStatusFromSponsorInfo, normalizeSubscriptionStatus } from "./subscriptionStatus.mjs";
import { DateTime } from "luxon";

// src/paymentMethod/individual.ts
function paymentMethodIndividualFromSnapshot(snap, now) {
	const trialStartDate = snap.trialStartDate ?? null;
	const trialEndDate = snap.trialEndDate ?? null;
	const trialStatus = trialEndDate ? DateTime.fromJSDate(trialEndDate).endOf("day").diff(now, "days").days >= 0 ? "active" : "expired" : "none";
	const trialActive = trialStatus === "active";
	const daysUntilTrialEndReal = trialEndDate ? Math.ceil(DateTime.fromJSDate(trialEndDate).endOf("day").diff(now.startOf("day"), "days").days) : null;
	const daysUntilTrialEnd = daysUntilTrialEndReal !== null ? Math.max(-1, daysUntilTrialEndReal) : null;
	const paymentCompleted = snap.hasSignupPayment;
	const subscriptionStatusFromCustomer = snap.signupPaymentEndDate && !snap.hasSubscription ? DateTime.fromJSDate(snap.signupPaymentEndDate).diff(now, "days").days > -1 ? "active" : "expired" : "inactive";
	const subscriptionStatusFromTrial = trialActive ? "active" : "inactive";
	const subscriptionStatusFromSubscription = snap.hasSubscription ? normalizeSubscriptionStatus(snap.subscriptionStatus) : "inactive";
	const subscriptionStatusFromSponsorInfo = getSubscriptionStatusFromSponsorInfo(snap.sponsorInfo, snap.signupFlowCompleted, now);
	const subscriptionStatuses = [
		subscriptionStatusFromTrial,
		subscriptionStatusFromSubscription,
		subscriptionStatusFromCustomer,
		subscriptionStatusFromSponsorInfo
	];
	const subscriptionStatus = subscriptionStatuses.includes("active") ? "active" : subscriptionStatuses.includes("expired") ? "expired" : "inactive";
	const subscriptionActive = subscriptionStatus === "active";
	const subscriptionFrom = subscriptionStatusFromSubscription === "active" ? "subscription" : subscriptionStatusFromCustomer === "active" ? "customer" : subscriptionStatusFromSponsorInfo === "active" ? "sponsorInfo" : "none";
	const consultationIncluded = snap.signupFlowCompleted ? subscriptionStatusFromSponsorInfo === "active" && snap.sponsorInfo?.consultationIncluded || subscriptionStatusFromSubscription === "active" && snap.stripeProductConsultation || false : snap.sponsorInfo?.consultationIncluded ?? false;
	const subscriptionPlanType = {
		subscription: "subscription",
		customer: "subscription",
		sponsorInfo: "sponsored",
		none: null
	}[subscriptionFrom];
	const subscriptionPlanNames = {
		subscription: snap.productName || "Not Selected",
		customer: snap.productName || "Not Selected",
		sponsorInfo: snap.sponsorInfo ? consultationIncluded ? "Greatly Complete Care (Sponsored)" : "Greatly Classes Only (Sponsored)" : null,
		none: null
	};
	const subscriptionScheduled = ["activated", "pending"].includes(snap.subscriptionScheduleStatus ?? "");
	const placeholderForSubscriptionInfo = snap.sponsorInfo ? {
		planName: subscriptionPlanNames.sponsorInfo,
		lastPeriodEnd: snap.sponsorInfo.endDate,
		lastPeriodStart: snap.sponsorInfo.startDate,
		automaticallyRenewing: false,
		sponsorSubscriptionStatus: subscriptionStatusFromSponsorInfo
	} : null;
	const reviewCue = !subscriptionActive && !trialActive || trialActive && !subscriptionScheduled || snap.subscriptionStatus === "past_due";
	const neverSubscribedAndNoTrial = !snap.hasSignupPayment && !snap.hasSubscription && !snap.sponsorInfo && !trialActive;
	return {
		showPlanSelectionAndStripeCheckout: !trialActive,
		neverSubscribedAndNoTrial,
		paymentCompleted: paymentCompleted || trialActive,
		paymentNeeded: !trialActive,
		reviewCue,
		subscriptionStatus,
		subscriptionActive,
		subscriptionFrom,
		subscriptionPlanName: subscriptionPlanNames[subscriptionFrom],
		subscriptionPlanNames,
		subscriptionPlanType,
		placeholderForSubscriptionInfo,
		consultationIncluded,
		sponsorEndDate: null,
		daysUntilSponsorEnd: null,
		isInSponsorTransitionWindow: false,
		subscriptionScheduleStatus: null,
		trialStatus,
		trialStartDate,
		trialEndDate,
		trialActive,
		daysUntilTrialEnd
	};
}
const buildPaymentMethodIndividualParams = (customer, trialInfo, subscription, sponsorInfo, product, _subscriptionSchedule) => {
	return paymentMethodIndividualFromSnapshot(extractPaymentMethodInputSnapshot({
		paymentMethodKind: "default",
		signupPaymentInfo: customer?.signupPaymentInfo ?? null,
		signupFlowCompleted: customer?.signupFlowCompleted ?? false,
		trialInfo,
		subscription,
		sponsorInfo,
		product,
		subscriptionSchedule: null
	}), DateTime.now());
};

export { buildPaymentMethodIndividualParams, paymentMethodIndividualFromSnapshot };
//# sourceMappingURL=individual.mjs.map